import { CogIcon, TrashIcon } from "@heroicons/react/outline";
import { Button } from "../buttons/Button";
import { Input } from "../input/Input";
import { styled } from "../../config/stiches.config";
import { Text } from "../text/Text";
import { useMemo, useRef } from "react";

export const QuestionBlock = styled("div", {
  display: "block",
  px: "$10",
  position: "relative",
});

export const QuestionBody = styled("div", {
  display: "block",
});

export const QuestionHeader = styled("div", {
  display: "flex",
  position: "relative",
  justifyContent: "space-between",
  alignItems: "center",
});

export const QuestionBlocks = styled("div", {
  display: "flex",
  alignItems: "center",
  columnGap: "16px",
});

export const QuestionErrorsBlock = styled("div", {
  px: "$10",
  mt: "$5",
});

export const InputPoints = styled(Input, {
  maxWidth: "56px",
  maxHeight: "40px",
  fontSize: "$3",
  variants: {
    hasError: {
      true: {
        border: "1px solid $red600",
        px: "4px !important",
      },
    },
  },
});

export const InputText = styled(Input, {
  border: "none",
  px: "1px !important",
  my: "32px",
  fontSize: "$3",
  height: "100%",
  minHeight: "60px",
  color: "$colors$passport",
  cursor: "pointer",
  ["&:focus"]: {
    outline: "none",
  },
  ["&:active"]: {
    outline: "none",
  },
  variants: {
    hasError: {
      true: {
        "&::placeholder": {
          color: "$red600",
        },
      },
    },
  },
});

export const InputLabel = styled(Text, {
  textAlign: "left",
});

export const DoubleAnswerBox = styled("div", {
  display: "flex",
  columnGap: "16px",
  mt: "8px",
});

export const SelectedAnswerBox = styled("button", {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
  border: "1px solid $gray300",
  borderRadius: "$radii$3",
  height: "60px",
  px: "26px",
  outline: "none",
  backgroundColor: "white",
  textAlign: "left",
  fill: "$colors$lime600",
  // ['&:hover']: {
  //     backgroundColor: '$colors$teal700',
  // },
  ["&:focus"]: {
    outline: "1px solid #6C7C8F",
  },
  ['&[data-selected="true"]']: {
    outline: "none",
    backgroundColor: "$colors$lime100",
    borderColor: "$colors$lime600",
  },
  ["&:focus-within"]: {
    outline: "1px solid $gray300",
    fill: "$colors$lime600",
  },
});

export const SelectedAnswerDiv = styled("div", {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
  border: "1px solid $gray300",
  borderRadius: "$radii$3",
  backgroundColor: "white",
  minHeight: "60px",
  px: "26px",
  outline: "none",
  textAlign: "left",
  fill: "$colors$lime600",
  // ['&:hover']: {
  //     backgroundColor: '$colors$teal700',
  // },
  ["&:focus"]: {
    outline: "1px solid #6C7C8F",
  },
  ['&[data-selected="true"]']: {
    outline: "none",
    backgroundColor: "$colors$lime100",
    borderColor: "$colors$lime600",
  },

  ['&[data-placeholder="true"]']: {
    opacity: ".75",
    color: "$gray400",
    backgroundColor: "$gray50",
  },
  ["&:focus-within"]: {
    outline: "1px solid $teal600",
    border: "none",
    fill: "$colors$lime600",
  },
  variants: {
    type: {
      student: {
        ["&:focus"]: {
          outline: "1px solid #01AAB9",
        },
        ['&[data-selected="true"]']: {
          outline: "none",
          backgroundColor: "$colors$teal100",
          borderColor: "$colors$teal600",
          fill: "$colors$teal600",
        },
        ['&[data-placeholder="true"]']: {
          opacity: ".75",
          color: "$gray400",
          backgroundColor: "$gray50",
        },
        ["&:focus-within"]: {
          outline: "1px solid $teal600",
          border: "none",
          fill: "$colors$teal600",
        },
      },
      answer: {},
      answerNotSelected: {
        ['&[data-selected="true"]']: {
          outline: "none",
          backgroundColor: "white",
          borderColor: "$colors$lime600",
        },
      },
      wrong: {
        ["&:focus"]: {
          outline: "1px solid #01AAB9",
        },
        ['&[data-selected="true"]']: {
          outline: "none",
          backgroundColor: "$colors$red50",
          borderColor: "$colors$red600",
          fill: "$colors$red600",
        },
        ['&[data-placeholder="true"]']: {
          opacity: ".75",
          color: "$gray400",
          backgroundColor: "$gray50",
        },
        ["&:focus-within"]: {
          outline: "1px solid $red600",
          border: "none",
          fill: "$colors$red600",
        },
      },
    },
    hasError: {
      true: {
        border: "1px solid $red600",
      },
    },
    printView: {
      true: {},
    },
  },
  compoundVariants: [
    {
      printView: true,
      type: "wrong",
      css: {
        ['&[data-selected="true"]']: {
          outline: "none",
          backgroundColor: "$colors$red50",
          borderColor: "Black",
          fill: "Black",
        },
        ['&[data-placeholder="true"]']: {
          opacity: ".75",
          color: "$gray400",
          backgroundColor: "$gray50",
        },
        ["&:focus-within"]: {
          outline: "1px solid $red600",
          border: "none",
          fill: "Black",
        },
      },
    },
    {
      printView: true,
      type: "answer",
      css: {
        ['&[data-selected="true"]']: {
          outline: "none",
          backgroundColor: "$colors$gray200",
          borderColor: "Black",
          fill: "Black",
        },
        ['&[data-placeholder="true"]']: {
          opacity: ".75",
          color: "$gray400",
          backgroundColor: "$gray50",
        },
        ["&:focus-within"]: {
          backgroundColor: "$colors$gray200",
          border: "none",
          fill: "Black",
        },
      },
    },
    {
      printView: true,
      type: "answerNotSelected",
      css: {
        ['&[data-selected="true"]']: {
          outline: "none",
          backgroundColor: "white",
          borderColor: "black",
          fill: "black",
        },
        ['&[data-placeholder="true"]']: {
          opacity: ".75",
          color: "$gray400",
          backgroundColor: "$gray50",
        },
        ["&:focus-within"]: {
          backgroundColor: "white",
          border: "none",
          fill: "Black",
        },
      },
    },
  ],
});

export const ShowAnswerContainer = styled("div", {
  display: "flex",
  height: "100%",
  width: "100%",
  alignItems: "center",
  columnGap: "$4",
});

export const IndexCircle = styled("div", {
  maxHeight: "32px",
  maxWidth: "32px",
  height: "32px",
  weight: "32px",
  minHeight: "32px",
  minWidth: "32px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "$colors$gray200",
  borderRadius: "$full",
  fontSize: "$2",
  ['&[data-selected="true"]']: {
    outline: "none",
    backgroundColor: "$colors$lime600",
    color: "White",
  },
  mr: "$4",
  variants: {
    type: {
      student: {
        ['&[data-selected="true"]']: {
          outline: "none",
          backgroundColor: "$colors$teal600",
          color: "White",
        },
      },
      answerNotSelected: {
        ['&[data-selected="true"]']: {
          outline: "none",
          backgroundColor: "$colors$lime600",
          color: "White",
        },
      },
      answer: {},
      wrong: {
        ['&[data-selected="true"]']: {
          outline: "none",
          backgroundColor: "$colors$red600",
          color: "White",
        },
      },
    },
    size: {},
    printView: {
      true: {},
    },
  },
  compoundVariants: [
    {
      printView: true,
      type: "wrong",
      css: {
        ['&[data-selected="true"]']: {
          outline: "none",
          backgroundColor: "$colors$gray200",
          color: "Black",
        },
      },
    },
    {
      printView: true,
      type: "answer",
      css: {
        ['&[data-selected="true"]']: {
          outline: "none",
          backgroundColor: "Black",
          color: "White",
        },
      },
    },
    {
      printView: true,
      type: "answerNotSelected",
      css: {
        ['&[data-selected="true"]']: {
          outline: "none",
          backgroundColor: "Black",
          color: "White",
        },
      },
    },
  ],
});

export function SelectedAnswerBoxWrapped({
  selected,
  onClick,
  children,
  CheckCircleSVG,
  CheckCircleDottedSVG,
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
  CheckCircleSVG: React.FC<any>;
  CheckCircleDottedSVG: React.FC<any>;
}) {
  return (
    <SelectedAnswerBox onClick={onClick} data-selected={selected} type="button">
      {children}
      {selected ? (
        <CheckCircleSVG className="fill-lime-600" />
      ) : (
        <CheckCircleDottedSVG className="stroke-gray-400" />
      )}
    </SelectedAnswerBox>
  );
}

export function SelectedMultipleChoice({
  selected,
  onClick,
  children,
  isFocused,
  isPlaceholder,
  showDelete,
  onDelete,
  hideIcon,
  hasError,
  CheckCircleSVG,
  CheckCircleDottedSVG,
}: {
  isFocused?: boolean;
  selected: boolean;
  isPlaceholder?: boolean;
  showDelete?: boolean;
  onClick: (e: any) => void;
  children: React.ReactNode;
  onDelete?: () => void;
  hideIcon?: boolean;
  hasError?: boolean;
  CheckCircleSVG: React.FC<any>;
  CheckCircleDottedSVG: React.FC<any>;
}) {
  const parentRef = useRef<HTMLDivElement | null>(null);

  const MemoIconComponent = useMemo(() => {
    if (hideIcon) return null;

    if (selected || isFocused) {
      return <CheckCircleSVG className="fill-inherit" onClick={onClick} />;
    }
    return (
      <CheckCircleDottedSVG className="stroke-gray-400" onClick={onClick} />
    );
  }, [selected, isFocused, hideIcon]);

  const showDeleteArea = !isPlaceholder && showDelete;
  const showDeleteSpace = isPlaceholder && showDelete;
  return (
    <ShowAnswerContainer>
      <SelectedAnswerDiv
        ref={parentRef}
        data-selected={selected}
        data-placeholder={isPlaceholder}
        as="div"
        hasError={hasError}
      >
        {children}
        <button
          type="button"
          onClick={onClick}
          className="h-full w-[40px] flex items-center justify-center"
        >
          {!isPlaceholder && MemoIconComponent}
        </button>
      </SelectedAnswerDiv>
      {showDeleteArea && (
        <Button
          shape="fixedSquareLarge"
          variant="white"
          type="button"
          onClick={() => {
            onDelete?.();
          }}
        >
          <TrashIcon className="w-5 h-5" />
        </Button>
      )}
      {showDeleteSpace && <div className="after:content-['\200b'] w-[40px]" />}
    </ShowAnswerContainer>
  );
}

export function SelectedMatchingOption({
  selected,
  onClick,
  children,
  isFocused,
  isPlaceholder,
  showDelete,
  onDelete,
  hideIcon,
  showGradyedOut,
  hasError,
  CheckCircleDottedSVG,
  CheckCircleSVG,
}: {
  isFocused?: boolean;
  selected: boolean;
  isPlaceholder?: boolean;
  showDelete?: boolean;
  onClick: (e: any) => void;
  children: React.ReactNode;
  onDelete?: () => void;
  hideIcon?: boolean;
  showGradyedOut?: boolean;
  hasError?: boolean;
  CheckCircleSVG: React.FC<any>;
  CheckCircleDottedSVG: React.FC<any>;
}) {
  const parentRef = useRef<HTMLDivElement | null>(null);

  const MemoIconComponent = useMemo(() => {
    if (hideIcon) return null;

    if (selected || isFocused) {
      return <CheckCircleSVG className="fill-inherit" onClick={onClick} />;
    }
    return (
      <CheckCircleDottedSVG className="stroke-gray-400" onClick={onClick} />
    );
  }, [selected, isFocused, hideIcon]);

  const showDeleteArea = !isPlaceholder && showDelete;
  const showDeleteSpace = isPlaceholder && showDelete;
  return (
    <ShowAnswerContainer>
      <SelectedAnswerDiv
        ref={parentRef}
        data-selected={selected}
        data-placeholder={isPlaceholder || showGradyedOut}
        as="div"
        hasError={hasError}
      >
        {children}
        <button
          type="button"
          onClick={onClick}
          className="h-full w-[40px] flex items-center justify-center"
        >
          {!isPlaceholder && MemoIconComponent}
        </button>
      </SelectedAnswerDiv>
      {showDeleteArea && (
        <Button
          shape="fixedSquareLarge"
          variant="white"
          type="button"
          onClick={() => {
            onDelete?.();
          }}
        >
          <TrashIcon className="w-5 h-5" />
        </Button>
      )}
      {showDeleteSpace && <div className="after:content-['\200b'] w-[40px]" />}
    </ShowAnswerContainer>
  );
}
